import { debounceTime, distinctUntilChanged, map, flatMap } from 'rxjs/operators';
import { GridOptions } from 'ag-grid-community/dist/lib/entities/gridOptions';
import { ReplaySubject, Subscription, combineLatest, merge, of } from 'rxjs';
import { Component, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';

import { UserProfilesActionsMenuAgRendererComponent } from './user-profiles-actions-menu-ag-renderer.component';
import { UserProfilesSearchParams } from './user-profiles-search-params';
import { UserProfilesReadModel } from './user-profiles-readmodel';
import { CovanceGridColumn } from '../../covance-ag-grid';
import { UserProfilesStorage } from './user-profiles-storage';
import { UserProfile } from './user-profile';
import { GridColumns } from '../../shared/grid-columns';

@Component({
    selector: 'user-profiles',
    template: require('./user-profiles.component.html'),
    styles: [require('./user-profiles.component.scss')]
})
export class UserProfilesComponent {
    public results: UserProfile[];

    public firstNameSearchTerm = new FormControl('');
    public lastNameSearchTerm = new FormControl('');
    public userNameSearchTerm = new FormControl('');

    public currentPage: number;
    public totalPages: number;

    public columns: CovanceGridColumn[] = [
        {
            hide: true,
            headerName: 'UserId',
            field: 'userId'
        },
        {
            headerName: 'Last name',
            field: 'lastName'
        },
        {
            headerName: 'First name',
            field: 'firstName'
        },
        {
            headerName: 'Username',
            field: 'userName'
        },
        this.gridColumns.actionsMenuColumn({
            cellRendererFramework: UserProfilesActionsMenuAgRendererComponent,
            suppressFilter: true,
            suppressSorting: true
        })
    ];

    public gridOptions: GridOptions = {
    };

    private page = new ReplaySubject<number>(1);

    private readonly subscription: Subscription;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private storage: UserProfilesStorage,
        private gridColumns: GridColumns) {

        this.subscription = combineLatest(
            this.page.asObservable(),
            this.getFlow(this.firstNameSearchTerm),
            this.getFlow(this.lastNameSearchTerm),
            this.getFlow(this.userNameSearchTerm)
        ).pipe(
            map(([page, firstName, lastName, userName]: [number, string, string, string]) => {
                this.showLoadingOverlay();
                const searchParams: UserProfilesSearchParams = {
                    firstName: firstName,
                    lastName: lastName,
                    userName: userName,
                    page: page,
                };

                return searchParams;
            }),
            flatMap(searchParams => this.storage.loadPageOfUsers(searchParams))
        ).subscribe((userProfiles: UserProfilesReadModel) => {
            this.results = userProfiles.users;
            this.currentPage = userProfiles.currentPage;
            this.totalPages = userProfiles.totalPages;
            this.changeDetector.markForCheck();
        });

        this.page.next(1);
    }

    public ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public onFirstPageClicked() {
        this.setPage(1);
    }

    public onPreviousPageClicked() {
        this.setPage(this.currentPage - 1);
    }

    public onNextPagClicked() {
        this.setPage(this.currentPage + 1);
    }

    public onLastPageClicked() {
        this.setPage(this.totalPages);
    }

    private setPage(pageNum: number) {
        this.currentPage = pageNum;
        this.page.next(pageNum);
        this.showLoadingOverlay();
    }

    private showLoadingOverlay() {
        if (this.gridOptions && this.gridOptions.api) {
            this.gridOptions.api.showLoadingOverlay();
        }
    }

    private getFlow(term: FormControl) {
        return merge(
            of(''),
            term.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );
    }
}